/* ============================================================
   Dolcefrotto — Set-Rabatt im Warenkorb

   - Fügt ein vorgeschlagenes Tuch über die Standard-Cart-API
     (/cart/add.js) hinzu; Rabatt und Summe berechnet Shopify.
   - Speichert nichts im Browser. Enthält keinen Tracking-Code.
   ============================================================ */
(() => {
  if (customElements.get('df-cart-upsell')) return;

  /* Fortschritt des zuletzt gezeigten Standes, damit der Balken weiterläuft statt neu zu starten */
  const lastFill = {};
  /* Nach einem Neuaufbau des Warenkorbs nicht erneut einblenden */
  const seen = {};

  /* Desktop: Vorschläge der Schublade als senkrechte Kartenspalte links neben dem Warenkorb.
     Mobil bleibt alles in der Schublade. Die Spalte liegt in #CartDrawer und wird bei
     jedem Neuaufbau der Schublade mit ersetzt, es bleibt also nichts Veraltetes stehen. */
  const desktop = window.matchMedia('(min-width: 990px)');
  const placeSide = (el) => {
    if (!desktop.matches) return;
    const host = el.closest('#CartDrawer') || (el.closest('cart-drawer') || {}).firstElementChild;
    if (!host || el.parentElement && el.parentElement.classList.contains('dfu-side')) return;
    let side = host.querySelector(':scope > .dfu-side');
    if (!side) {
      side = document.createElement('aside');
      side.className = 'dfu-side';
      side.setAttribute('aria-label', 'Dazu kombinieren');
      host.appendChild(side);
    }
    const inner = host.querySelector('.drawer__inner');
    if (inner && inner.offsetWidth) side.style.right = `${inner.offsetWidth}px`;
    side.replaceChildren(el);
  };

  class DfCartUpsell extends HTMLElement {
    connectedCallback() {
      this.context = this.dataset.context || 'drawer';
      const key = `${this.context}:${this.dataset.part || 'all'}`;
      if (seen[key]) this.style.animation = 'none';
      seen[key] = true;

      const track = this.querySelector('[data-dfu-track]');
      const fill = Number(this.dataset.fill) || 0;
      const from = lastFill[this.context];
      if (track && typeof from === 'number' && from !== fill) {
        track.style.transition = 'none';
        track.style.width = `${from}%`;
        void track.offsetWidth;
        track.style.transition = '';
        requestAnimationFrame(() => {
          track.style.width = `${fill}%`;
        });
      }
      lastFill[this.context] = fill;

      if (this.context === 'drawer' && this.dataset.part === 'rail') placeSide(this);

      if (!this.bound) {
        this.bound = true;
        this.addEventListener('click', (event) => {
          const button = event.target.closest('[data-dfu-add]');
          if (button) this.add(button);
        });
      }

      /* Warenkorb-Seite: nach Mengenänderungen den eigenen Block neu laden */
      if (
        this.context === 'page' &&
        this.dataset.sectionId &&
        !this.unsubscribe &&
        typeof subscribe === 'function' &&
        typeof PUB_SUB_EVENTS !== 'undefined'
      ) {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, () => this.refresh());
      }
    }

    disconnectedCallback() {
      if (this.unsubscribe) this.unsubscribe();
      this.unsubscribe = null;
    }

    showError(message) {
      const node = this.querySelector('[data-dfu-error]');
      if (!node) return;
      node.hidden = !message;
      node.textContent = message || '';
    }

    async refresh() {
      try {
        const url = `${window.location.pathname}?section_id=${encodeURIComponent(this.dataset.sectionId)}`;
        const html = await (await fetch(url)).text();
        const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('df-cart-upsell.dfu--page');
        if (!fresh) {
          this.hidden = true;
          return;
        }
        this.hidden = false;
        const track = fresh.querySelector('[data-dfu-track]');
        const from = Number(this.dataset.fill) || 0;
        const to = Number(fresh.dataset.fill) || 0;
        if (track) track.style.width = `${from}%`;
        this.innerHTML = fresh.innerHTML;
        this.dataset.fill = to;
        lastFill.page = to;
        const live = this.querySelector('[data-dfu-track]');
        if (live) {
          void live.offsetWidth;
          requestAnimationFrame(() => {
            live.style.width = `${to}%`;
          });
        }
      } catch (error) {
        console.error('[df-cart-upsell] aktualisieren', error);
      }
    }

    async add(button) {
      if (this.busy) return;
      this.busy = true;
      this.classList.add('is-loading');
      button.classList.add('is-busy');
      this.showError('');

      const routes = window.routes || {};
      const drawer = this.context === 'drawer' ? this.closest('cart-drawer') : null;
      const payload = { items: [{ id: Number(button.dataset.dfuAdd), quantity: 1 }] };
      if (drawer) {
        payload.sections = drawer
          .getSectionsToRender()
          .map((section) => section.id)
          .join(',');
        payload.sections_url = window.location.pathname;
      }

      try {
        const response = await fetch(`${routes.cart_add_url || '/cart/add'}.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify(payload),
        });
        const data = await response.json();
        if (!response.ok || data.status) {
          this.showError(data.description || data.message || 'Das hat leider nicht geklappt.');
          return;
        }
        if (!drawer) {
          /* Warenkorb-Seite: mit dem neuen Stand neu laden */
          window.location.reload();
          return;
        }
        /* baut die Schublade mit dem neuen Stand neu auf (inklusive dieses Blocks) */
        drawer.renderContents(data);
      } catch (error) {
        console.error('[df-cart-upsell] hinzufügen', error);
        this.showError('Das hat leider nicht geklappt. Versuch es bitte nochmal.');
      } finally {
        this.busy = false;
        this.classList.remove('is-loading');
        button.classList.remove('is-busy');
      }
    }
  }

  customElements.define('df-cart-upsell', DfCartUpsell);

  /* ============================================================
     10 % für die Newsletter-Anmeldung (Klaviyo, einmaliger Code)
     1) E-Mail -> Klaviyo-Liste (client API, öffentliche Site-ID
        aus dem bereits geladenen Klaviyo-Skript, kein Schlüssel im Theme)
     2) Code aus der Willkommens-Mail hier einlösen -> /cart/update.js
     Merkt sich nur "Anmeldung abgeschickt" für diese Sitzung.
     ============================================================ */
  const DFS_KEY = 'dfs-signup';
  const readState = () => { try { return sessionStorage.getItem(DFS_KEY) || ''; } catch (e) { return ''; } };
  const writeState = (v) => { try { sessionStorage.setItem(DFS_KEY, v); } catch (e) { /* privat-modus */ } };
  const routes = () => window.routes || {};

  const klaviyoSite = () => {
    const tag = [...document.scripts].find((s) => /klaviyo\.js\?company_id=/.test(s.src || ''));
    const m = tag && tag.src.match(/company_id=([A-Za-z0-9]+)/);
    return m ? m[1] : '';
  };

  /* Code im Warenkorb einlösen. Behält andere gültige Codes, entfernt einen ungültigen wieder. */
  const applyCode = async (raw, host) => {
    const code = String(raw || '').trim().toUpperCase();
    if (!code) return { ok: false };
    const current = await (await fetch(`${routes().cart_url || '/cart'}.js`)).json();
    const codes = (current.discount_codes || [])
      .filter((d) => d.applicable && d.code.toUpperCase() !== code)
      .map((d) => d.code);
    codes.push(code);
    const drawer = (host && host.closest('cart-drawer')) || document.querySelector('cart-drawer');
    const payload = { discount: codes.join(',') };
    if (drawer && typeof drawer.getSectionsToRender === 'function') {
      payload.sections = drawer.getSectionsToRender().map((s) => s.id).join(',');
      payload.sections_url = window.location.pathname;
    }
    const res = await fetch(`${routes().cart_update_url || '/cart/update'}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload),
    });
    const data = await res.json();
    const hit = (data.discount_codes || []).find((d) => d.code.toUpperCase() === code);
    if (!res.ok || !hit || !hit.applicable) {
      if (hit && !hit.applicable) {
        fetch(`${routes().cart_update_url || '/cart/update'}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ discount: codes.filter((c) => c !== code).join(',') }),
        }).catch(() => {});
      }
      return { ok: false };
    }
    writeState('applied');
    if (drawer && data.sections && typeof drawer.renderContents === 'function') {
      drawer.renderContents(data);
    } else if (document.querySelector('df-cart-signup.dfs--page')) {
      window.location.reload();
    }
    return { ok: true, code };
  };

  /* ---------- Klaviyo-Formular: öffnen, Code abfangen, automatisch einlösen ----------
     Das Formular (Einwilligung, Double-Opt-in, einmaliger Code) gehört Klaviyo.
     Nach dem Absenden zeigt es den Code an; den lesen wir aus dem Formular und lösen ihn ein. */
  const CODE_RE = /^(?=.*\d)[A-Z0-9]{6,16}$/;
  let watching = null;

  const findCodeIn = (root) => {
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    let node;
    while ((node = walker.nextNode())) {
      const t = (node.nodeValue || '').trim();
      if (CODE_RE.test(t)) return t;
    }
    const inputs = root.querySelectorAll('input[readonly], input[type="text"]');
    for (const i of inputs) {
      const v = (i.value || '').trim();
      if (CODE_RE.test(v)) return v;
    }
    return '';
  };

  const klaviyoRoots = () =>
    [...document.querySelectorAll('[data-testid*="klaviyo-form"], .klaviyo-form, [class*="kl-private-reset-css"]')]
      .filter((el) => !el.closest('df-cart-signup'));

  const watchForCode = (formId) => {
    if (watching) return;
    const started = Date.now();
    const tick = async () => {
      if (Date.now() - started > 60000) { watching = null; return; }
      let code = '';
      for (const root of klaviyoRoots()) {
        code = findCodeIn(root);
        if (code) break;
      }
      if (!code) { watching = setTimeout(tick, 400); return; }
      watching = null;
      writeState('code:' + code);
      const box = document.querySelector('cart-drawer df-cart-signup') || document.querySelector('df-cart-signup');
      if (box && box.say) box.say('Dein Code ' + code + ' wird abgezogen …');
      try {
        const r = await applyCode(code, box);
        const fresh = document.querySelector('cart-drawer df-cart-signup') || document.querySelector('df-cart-signup');
        if (!r.ok && fresh && fresh.showCode) fresh.showCode(code);
      } catch (e) {
        const fresh = document.querySelector('df-cart-signup');
        if (fresh && fresh.showCode) fresh.showCode(code);
      }
    };
    watching = setTimeout(tick, 400);
  };

  /* ---------- Warenkorb frei halten ----------
     Solange der Warenkorb offen ist, verdecken weder das Klaviyo-Pop-up noch der Chat
     den Kasse-Button (CSS in df-cart-signup.css). Ausnahme: Der Kunde tippt selbst auf
     "10 % sichern" (html.dfs-kl-open). */
  const html = document.documentElement;
  const syncCartOpen = () => {
    const drawer = document.querySelector('cart-drawer');
    const open = (drawer && drawer.classList.contains('active')) || /\/cart\/?$/.test(window.location.pathname);
    html.classList.toggle('dfs-cart-open', !!open);
    if (!open) html.classList.remove('dfs-kl-open');
  };
  const watchDrawer = () => {
    const drawer = document.querySelector('cart-drawer');
    if (drawer) new MutationObserver(syncCartOpen).observe(drawer, { attributes: true, attributeFilter: ['class'] });
    syncCartOpen();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', watchDrawer);
  else watchDrawer();

  let pendingForm = '';
  window.addEventListener('klaviyoForms', (e) => {
    const d = e.detail || {};
    if (d.type === 'close') html.classList.remove('dfs-kl-open');
    if (!pendingForm || (d.formId && d.formId !== pendingForm)) return;
    if (d.type === 'submit' || d.type === 'stepSubmit') watchForCode(pendingForm);
  });

  class DfCartSignup extends HTMLElement {
    connectedCallback() {
      if (this.bound) return;
      this.bound = true;
      this.context = this.dataset.context || 'drawer';
      this.msg = this.querySelector('[data-dfs-msg]');
      if (!this.querySelector('[data-dfs-step]')) return; /* Code bereits aktiv */
      const state = readState();
      if (state.startsWith('code:')) this.showCode(state.slice(5));
      else if (state === 'sent') this.show(2);

      const emailForm = this.querySelector('[data-dfs-email]');
      const codeForm = this.querySelector('[data-dfs-code]');
      const have = this.querySelector('[data-dfs-have]');
      const open = this.querySelector('[data-dfs-open]');
      if (emailForm) emailForm.addEventListener('submit', (e) => { e.preventDefault(); this.subscribe(emailForm); });
      if (codeForm) codeForm.addEventListener('submit', (e) => { e.preventDefault(); this.apply(codeForm); });
      if (have) have.addEventListener('click', () => { this.show(2, true); });
      if (open) open.addEventListener('click', () => this.openKlaviyo());
    }

    openKlaviyo() {
      const id = this.dataset.form;
      if (!id) return;
      pendingForm = id;
      html.classList.add('dfs-kl-open');
      window._klOnsite = window._klOnsite || [];
      window._klOnsite.push(['openForm', id]);
      /* Falls das Klaviyo-Skript (z. B. ohne Einwilligung) nicht lädt: auf das Code-Feld ausweichen */
      setTimeout(() => {
        if (!klaviyoRoots().length) {
          this.show(2, true);
          this.say('Das Anmeldeformular konnte nicht geladen werden. Hast du schon einen Code, kannst du ihn hier einlösen.', true);
        }
      }, 2500);
    }

    showCode(code) {
      this.show(2, true);
      const input = this.querySelector('[data-dfs-code] input');
      if (input) input.value = code;
      this.say('Dein Code ist da. Tippe auf „Einlösen“.');
    }

    show(step, haveCode) {
      this.querySelectorAll('[data-dfs-step]').forEach((el) => { el.hidden = el.dataset.dfsStep !== String(step); });
      if (step === 2 && haveCode) {
        const t = this.querySelector('[data-dfs-step2-title]');
        const s = this.querySelector('[data-dfs-step2-sub]');
        if (t) t.textContent = 'Code einlösen';
        if (s) s.textContent = 'Gib deinen Rabattcode ein, er wird sofort abgezogen.';
      }
      const input = this.querySelector(`[data-dfs-step="${step}"] input`);
      if (input && haveCode && !input.value) input.focus();
      this.say('');
    }

    say(text, isError) {
      if (!this.msg) return;
      this.msg.hidden = !text;
      this.msg.textContent = text || '';
      this.msg.classList.toggle('is-error', !!isError);
    }

    busy(form, on) {
      const btn = form.querySelector('button');
      form.classList.toggle('is-busy', on);
      if (btn) btn.disabled = on;
    }

    async subscribe(form) {
      const input = form.querySelector('input');
      const email = (input.value || '').trim();
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email)) {
        this.say('Bitte gib eine gültige E-Mail-Adresse ein.', true);
        input.focus();
        return;
      }
      const site = klaviyoSite();
      if (!site) {
        this.say('Das hat gerade nicht geklappt. Versuch es bitte gleich nochmal.', true);
        return;
      }
      this.busy(form, true);
      try {
        const res = await fetch(`https://a.klaviyo.com/client/subscriptions/?company_id=${encodeURIComponent(site)}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/vnd.api+json', Accept: 'application/vnd.api+json', revision: '2024-10-15' },
          body: JSON.stringify({
            data: {
              type: 'subscription',
              attributes: {
                custom_source: `Warenkorb ${this.dataset.pct || 10} %`,
                profile: {
                  data: {
                    type: 'profile',
                    attributes: { email, subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } } },
                  },
                },
              },
              relationships: { list: { data: { type: 'list', id: this.dataset.list } } },
            },
          }),
        });
        if (!res.ok) throw new Error(`Klaviyo ${res.status}`);
        writeState('sent');
        this.show(2);
      } catch (error) {
        console.error('[df-cart-signup] anmelden', error);
        this.say('Das hat leider nicht geklappt. Versuch es bitte nochmal.', true);
      } finally {
        this.busy(form, false);
      }
    }

    async apply(form) {
      const input = form.querySelector('input');
      if (!(input.value || '').trim()) { input.focus(); return; }
      this.busy(form, true);
      this.say('');
      try {
        const r = await applyCode(input.value, this);
        if (!r.ok) this.say('Dieser Code passt leider nicht. Prüf ihn bitte nochmal.', true);
      } catch (error) {
        console.error('[df-cart-signup] einlösen', error);
        this.say('Das hat leider nicht geklappt. Versuch es bitte nochmal.', true);
      } finally {
        if (this.isConnected) this.busy(form, false);
      }
    }
  }

  if (!customElements.get('df-cart-signup')) customElements.define('df-cart-signup', DfCartSignup);
})();
